import { Avatar, Stack, Typography } from '@optimus-web/core';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { LocalizedDateTimeFromUtc } from '@mews-commander/ui';
import { useGetAvatarSrc } from '@mews-commander/utils';
import { RateGroupWithProfiles } from '../types';
import { ListItem } from './ListItem';

interface DetailsTabContentProps {
    rateGroup?: Pick<
        RateGroupWithProfiles,
        'ordering' | 'creatorProfile' | 'updaterProfile' | 'createdUtc' | 'updatedUtc'
    > & {
        description?: string | null;
        shortName?: string | null;
        rateGroupKey?: string | null;
    };
    enterpriseName?: string | null;
    rates: number;
    serviceKey?: string | null;
    editRateGroupButton: React.ReactNode;
}

interface UserAvatarProps {
    name?: string | null;
    imageId?: string | null;
}

const UserAvatar = ({ name, imageId }: UserAvatarProps) => {
    const getAvatarSrc = useGetAvatarSrc();
    const avatarImage = getAvatarSrc(imageId);

    return (
        <Stack spacing="100" itemAlignment="center">
            <Avatar src={avatarImage} title={name || ''} size="small" />
            <span>{name}</span>
        </Stack>
    );
};

export const DetailsTabContent = ({
    rateGroup,
    enterpriseName,
    serviceKey,
    rates,
    editRateGroupButton,
}: DetailsTabContentProps) => {
    const data = {
        ordering: rateGroup?.ordering,
        creatorProfile: rateGroup?.creatorProfile,
        updaterProfile: rateGroup?.updaterProfile,
        createdOn: rateGroup?.createdUtc,
        updatedOn: rateGroup?.updatedUtc,
        description: rateGroup?.description,
        shortName: rateGroup?.shortName,
        rateGroupKey: rateGroup?.rateGroupKey,
    };

    return rateGroup ? (
        <>
            <Stack itemAlignment="center" distribution="space-between">
                <Typography textStyle="titleSmallStrong" as="div">
                    <LocalizedText textKey={TextKey.DetailPlural} />
                </Typography>
                {editRateGroupButton}
            </Stack>
            {data.description ? (
                <Stack vertical spacing="200">
                    <Typography textStyle="bodyLarge" as="div">
                        {data.description}
                    </Typography>
                </Stack>
            ) : null}
            <Stack vertical spacing="200">
                {enterpriseName ? <ListItem rowLabelTextKey={TextKey.Property} data={enterpriseName} /> : null}
                <ListItem rowLabelTextKey={TextKey.ServiceKey} data={serviceKey} />
                {rates ? <ListItem rowLabelTextKey={TextKey.RatePlural} data={rates} /> : null}
                <ListItem rowLabelTextKey={TextKey.RateGroupKey} data={data.rateGroupKey} />
                <ListItem rowLabelTextKey={TextKey.ShortName} data={data.shortName} />
                <ListItem rowLabelTextKey={TextKey.Ordering} data={data.ordering} />
                <ListItem rowLabelTextKey={TextKey.CreatedBy}>
                    {data.creatorProfile ? (
                        <UserAvatar imageId={data.creatorProfile.imageId} name={data.creatorProfile.name} />
                    ) : null}
                </ListItem>
                <ListItem
                    rowLabelTextKey={TextKey.CreatedOn}
                    data={data.createdOn ? <LocalizedDateTimeFromUtc utcString={data.createdOn} /> : null}
                />
                <ListItem rowLabelTextKey={TextKey.UpdatedBy}>
                    {data.updaterProfile ? (
                        <UserAvatar imageId={data.updaterProfile.imageId} name={data.updaterProfile.name} />
                    ) : null}
                </ListItem>
                <ListItem
                    rowLabelTextKey={TextKey.UpdatedOn}
                    data={data.updatedOn ? <LocalizedDateTimeFromUtc utcString={data.updatedOn} /> : null}
                    omitDivider
                />
            </Stack>
        </>
    ) : null;
};
